import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, type AuthAdapter } from '@ochal/auth';
import type { PluginRuntime } from '@ochal/plugin-runtime';
import { ContributionErrorBoundary, PlatformRuntimeProvider } from '@ochal/react-runtime';
import { ToastProvider } from '@ochal/ui';
import { fa } from './i18n/fa';
import { useUIStore } from './store/ui-store';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } });
export function AppProviders({ runtime, authAdapter, children }: { runtime: PluginRuntime; authAdapter: AuthAdapter; children: ReactNode }) {
  const theme = useUIStore((state) => state.theme);
  const density = useUIStore((state) => state.density);
  const accent = useUIStore((state) => state.accent);
  useEffect(() => {
    const onAppearance = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, string>>).detail;
      const state = useUIStore.getState();
      if (detail.theme === 'light' || detail.theme === 'dark' || detail.theme === 'system') state.setTheme(detail.theme);
      if (detail.accent === 'navy' || detail.accent === 'blue' || detail.accent === 'teal') state.setAccent(detail.accent);
      if (detail.density === 'compact' || detail.density === 'comfortable') state.setDensity(detail.density);
    };
    addEventListener('ochal:appearance', onAppearance);
    return () => removeEventListener('ochal:appearance', onAppearance);
  }, []);
  useEffect(() => {
    const media = typeof matchMedia === 'function'
      ? matchMedia('(prefers-color-scheme: dark)')
      : { matches: false, addEventListener: () => undefined, removeEventListener: () => undefined };
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && media.matches);
      document.documentElement.classList.toggle('dark', dark);
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
      document.documentElement.dataset.density = density;
      document.documentElement.dataset.accent = accent;
      document.documentElement.dataset.theme = theme;
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [accent, density, theme]);
  const fallback = <main className="error-page"><h1>{fa.state.unexpectedTitle}</h1><p>{fa.state.unexpectedDescription}</p><button onClick={() => location.reload()}>{fa.state.reload}</button></main>;
  return <ContributionErrorBoundary fallback={fallback} onError={(error) => runtime.observability.error('Application render failed', error)}><PlatformRuntimeProvider runtime={runtime}><QueryClientProvider client={queryClient}><AuthProvider adapter={authAdapter} events={runtime.events}><ToastProvider>{children}</ToastProvider></AuthProvider></QueryClientProvider></PlatformRuntimeProvider></ContributionErrorBoundary>;
}
export function StartupScreen() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => { const on = () => setOffline(false); const off = () => setOffline(true); addEventListener('online', on); addEventListener('offline', off); return () => { removeEventListener('online', on); removeEventListener('offline', off); }; }, []);
  return offline ? <div className="offline-banner" role="status">{fa.state.offline}</div> : null;
}
